import Styles from './PriceComponentContainer.module.css'
import Colors from 'Theme/Colors'
import {
  IFSelect,
  IFText,
  IFTextInput,
  RestrictionsAccordion,
} from 'Components'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfinityEnums from 'Enums/InfinityEnums'
import styled from 'styled-components'
import { DoNotDisturbOn } from '@mui/icons-material'
import validator from 'validator'
import PropTypes from 'prop-types'

const StyledDiv = styled.div`
  &:hover {
    color: whitesmoke;
  }
`

const defaultPriceComponent = () => ({
  id: crypto?.randomUUID?.() ?? String(Date.now() + Math.random()),
  type: '',
  vat: '',
  price: '',
  stepSize: '',
})
const pcToGroup = (pc) => ({
  id: crypto?.randomUUID?.() ?? String(Date.now() + Math.random()),
  type: pc?.type ?? '',
  vat: pc?.vat != null ? String(pc.vat) : '',
  price: pc?.price != null ? String(pc.price) : '',
  stepSize: pc?.stepSize != null ? String(pc.stepSize) : '',
})

const PriceComponentContainer = ({
  restrictions,
  priceComponents,
  onChange,
  onErrorChange,
  operator,
}) => {
  const { t } = useTranslation()
  const buttonRef = useRef()

  const [components, setComponents] = useState([defaultPriceComponent()])
  const [localRestrictions, setLocalRestrictions] = useState(
    restrictions && !Array.isArray(restrictions) ? restrictions : null,
  )
  const [errors, setErrors] = useState({})
  const [typeError, setTypeError] = useState(undefined)

  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    onErrorChange?.(hasError)
  }, [hasError, onErrorChange])

  useEffect(() => {
    if (!Array.isArray(priceComponents) || priceComponents.length === 0) {
      setComponents([defaultPriceComponent()])
      return
    }
    const mapped = priceComponents.map((r) => {
      const pc =
        Array.isArray(r?.priceComponents) && r.priceComponents.length
          ? r.priceComponents
          : r
      return pcToGroup(pc)
    })

    setComponents(mapped)
  }, [])
  useEffect(() => {
    if (!onChange) return

    const priceComponents = components.map((grp) => ({
      type: grp.type,
      price: grp.price,
      vat: grp.vat,
      stepSize: grp.stepSize,
    }))

    const payload = {
      priceComponents,
      ...(localRestrictions &&
      typeof localRestrictions === 'object' &&
      !Array.isArray(localRestrictions) &&
      Object.keys(localRestrictions).length > 0
        ? { restrictions: localRestrictions }
        : {}),
    }
    onChange(payload)
  }, [localRestrictions, components])

  const updateField = (idx, field, value) => {
    setComponents((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      return next
    })
  }
  const setError = (idx, field, errorMessage) => {
    setErrors((prev) => ({
      ...prev,
      [idx]: {
        ...prev[idx],
        [field]: errorMessage,
      },
    }))
  }

  const addComponent = () => {
    setComponents((prev) => [...prev, defaultPriceComponent()])
  }

  const removeComponent = (idx) => {
    setComponents((prev) => prev.filter((_, i) => i !== idx))
    setErrors((prev) => {
      const next = { ...prev }
      delete next[idx]
      const reindexed = {}
      Object.keys(next).forEach((key) => {
        const numKey = parseInt(key)
        if (numKey > idx) {
          reindexed[numKey - 1] = next[key]
        } else {
          reindexed[numKey] = next[key]
        }
      })
      return reindexed
    })
  }

  return (
    <div className={Styles.Container}>
      <div
        className={Styles.ContentContainer}
        style={{ border: '1px solid #E0E0E0' }}
      >
        {components.map((pc, idx) => (
          <div key={pc.id} className={Styles.Block}>
            <div className={Styles.TitleContainer}>
              {components.length > 1 && (
                <DoNotDisturbOn
                  className={Styles.RemoveIcon}
                  style={{
                    color: Colors.ChargePointTariffListItem.RemoveIcon,
                  }}
                  onClick={() => removeComponent(idx)}
                />
              )}
              <IFText
                className={Styles.TitleText}
                style={{ color: Colors.text }}
              >
                {t('TariffModal.PriceComponent')}
              </IFText>
            </div>
            <div
              className={Styles.InputContainer}
              style={
                components.length === 1 ? { marginTop: '1rem' } : undefined
              }
            >
              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.Type')}
                </IFText>
                <div className={Styles.FieldAbs}>
                  <IFSelect
                    menuMaxHeight={100}
                    data={
                      InfinityEnums?.TariffType
                        ? Object.values(InfinityEnums.TariffDimensionType).map(
                            (type) => ({
                              label: type,
                              value: type,
                            }),
                          )
                        : []
                    }
                    value={pc.type}
                    onChange={(val) => {
                      updateField(idx, 'type', val)

                      if (!val || val.length === 0) {
                        setError(idx, 'type', t('TariffModal.Required'))
                      } else {
                        setTypeError('')
                      }
                    }}
                    handleClosePicker={() => {
                      if (!pc.type || pc.type.length === 0) {
                        setError(idx, 'type', t('TariffModal.Required'))
                      } else {
                        setError(idx, 'type', undefined)
                      }
                    }}
                    style={{ minWidth: '12rem', maxWidth: '12rem' }}
                    size="sm"
                    canLoadMore={false}
                    searchable={false}
                  />
                  {errors[idx]?.type && (
                    <div className={Styles.ErrorText}>{errors[idx]?.type}</div>
                  )}
                </div>
              </div>

              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.VAT')}
                </IFText>
                <div className={Styles.WithSuffix}>
                  <IFTextInput
                    className={Styles.TextInput}
                    value={pc.vat}
                    onChange={(e) => {
                      updateField(idx, 'vat', e.target.value)
                      if (e.target.value === '') {
                        setError(idx, 'vat', undefined)
                        setHasError(false)
                        return
                      }

                      if (!validator.isFloat(e.target.value)) {
                        setError(idx, 'vat', t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }

                      if (!validator.isFloat(e.target.value, { min: 0.1 })) {
                        setError(idx, 'vat', t('TariffModal.Invalid'))
                        setHasError(true)
                        return
                      }
                      setError(idx, 'vat', undefined)
                      setHasError(false)
                    }}
                    errorText={errors[idx]?.vat}
                    placeholder={t('TariffModal.Vat')}
                  />
                </div>
              </div>
            </div>
            <div className={Styles.InputContainer}>
              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.Price')}
                </IFText>
                <IFTextInput
                  className={Styles.TextInput}
                  value={pc.price}
                  onChange={(e) => {
                    const value = e.target.value
                    if (value === '') {
                      updateField(idx, 'price', value)
                      setError(idx, 'price', undefined)
                      setHasError(false)
                      return
                    }
                    const decimalPlaces =
                      operator?.country?.currency?.decimalPlaces ?? 2
                    const decimalPattern = new RegExp(
                      `^\\d*\\.?\\d{0,${decimalPlaces}}$`,
                    )

                    if (!decimalPattern.test(value)) {
                      return
                    }
                    updateField(idx, 'price', value)
                    if (!validator.isFloat(value, { min: 0 })) {
                      setError(idx, 'price', t('TariffModal.Invalid'))
                      setHasError(true)
                      return
                    }

                    setError(idx, 'price', undefined)
                    setHasError(false)
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || !e.target.value) {
                      setError(idx, 'price', t('TariffModal.Required'))
                      setHasError(true)
                    }
                  }}
                  errorText={errors[idx]?.price}
                  placeholder={t('TariffModal.Price')}
                />
              </div>

              <div className={Styles.InputItem}>
                <IFText
                  className={Styles.InputLabel}
                  style={{ color: Colors.DetailPanel }}
                >
                  {t('TariffModal.StepSize')}
                </IFText>
                <IFTextInput
                  className={Styles.TextInput}
                  value={pc.stepSize}
                  onChange={(e) => {
                    updateField(idx, 'stepSize', e.target.value)

                    if (e.target.value === '') {
                      setError(idx, 'stepSize', undefined)
                      setHasError(false)
                      return
                    }

                    if (!validator.isFloat(e.target.value)) {
                      setError(idx, 'stepSize', t('TariffModal.Invalid'))
                      setHasError(true)
                      return
                    }

                    if (!validator.isFloat(e.target.value, { min: 0.1 })) {
                      setError(idx, 'stepSize', t('TariffModal.Invalid'))
                      setHasError(true)
                      return
                    }
                    setError(idx, 'stepSize', undefined)
                    setHasError(false)
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || !e.target.value) {
                      setError(idx, 'stepSize', t('TariffModal.Required'))
                      setHasError(true)
                    }
                  }}
                  errorText={errors[idx]?.stepSize}
                  placeholder={t('TariffModal.StepSize')}
                />
              </div>
            </div>
            <div
              className={Styles.VerticalDivider}
              style={{ backgroundColor: Colors.grey }}
            />
          </div>
        ))}

        <div className={Styles.AddButtonDiv}>
          <button
            className={Styles.AddButton}
            style={{ color: Colors.primary }}
            onClick={addComponent}
            ref={buttonRef}
          >
            <StyledDiv
              className={Styles.IconContainer}
              style={{ color: Colors.primary }}
            >
              <IFText className={Styles.AddButtonText}>
                {t('TariffModal.AddPriceComponent')}
              </IFText>
            </StyledDiv>
          </button>
        </div>

        <div className={Styles.RestrictionDiv}>
          <RestrictionsAccordion
            onChange={setLocalRestrictions}
            restrictions={restrictions}
            onErrorChange={(error) => {
              setHasError(error)
            }}
          />
        </div>
      </div>
    </div>
  )
}

PriceComponentContainer.propTypes = {
  restrictions: PropTypes.array,
  onChange: PropTypes.func,
  priceComponents: PropTypes.array,
  onErrorChange: PropTypes.func,
}

export default PriceComponentContainer
